import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Crown, MapPin, Wallet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ironlakBgPath from "@assets/Iornlak background2_1754967939393.jpg";

export default function Landing() {
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const { toast } = useToast();

  // Check if already connected on load
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then((accounts: string[]) => {
          if (accounts.length > 0) {
            setIsConnected(true);
            setWalletAddress(accounts[0]);
          }
        });
    }
  }, []);

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ 
          method: 'eth_requestAccounts' 
        });
        setIsConnected(true);
        setWalletAddress(accounts[0]);
        toast({
          title: "Wallet Connected",
          description: `Connected to ${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}`,
        });
      } catch (error) {
        toast({
          title: "Connection Failed",
          description: "Failed to connect to Metamask wallet",
          variant: "destructive",
        });
      }
    } else {
      window.open('https://metamask.io/download/', '_blank');
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative bg-black text-white">
      {/* Ironlak Graffiti Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center" 
        style={{ backgroundImage: `url(${ironlakBgPath})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
      
      {/* Metamask Connect Button - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <Button 
          variant="outline"
          onClick={connectWallet}
          className={`flex items-center gap-2 text-white border-white/30 hover:border-white/60 ${
            isConnected 
              ? 'bg-green-600 hover:bg-green-700 border-green-500' 
              : 'bg-black/60 backdrop-blur-sm hover:bg-black/80'
          }`}
          data-testid="button-metamask-connect"
        >
          <Wallet size={16} />
          {isConnected 
            ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` 
            : 'Connect Metamask'
          }
        </Button>
      </div>
      
      {/* Header */}
      <header className="relative z-10 pt-20 pb-8 text-center">
        <h1 className="text-5xl font-bold mb-3 text-white drop-shadow-2xl" data-testid="title-lpfts">
          LPFTS WORLD WIDE™
        </h1>
        <p className="text-white text-xl font-medium drop-shadow-lg" data-testid="subtitle-app">
          Street Art Geocaching Game
        </p>
      </header>
      
      {/* Main Content */}
      <main className="relative z-10 flex-1 flex flex-col justify-center px-6 pb-8 max-w-6xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          
          {/* YouTube Video Section */}
          <div className="order-2 lg:order-1">
            <Card className="bg-black/70 backdrop-blur-sm border-white/20 shadow-2xl">
              <CardContent className="p-4">
                <div className="aspect-video w-full">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/wvq1QuWh9FQ"
                    title="LPFTS World Wide Trailer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="rounded-lg"
                  ></iframe>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Login Section */}
          <div className="order-1 lg:order-2">
            <Card className="bg-black/70 backdrop-blur-sm border-white/20 shadow-2xl">
              <CardContent className="pt-6">
                <h2 className="text-3xl font-bold mb-4 text-center text-white drop-shadow-lg" data-testid="heading-join">
                  Join the Revolution
                </h2>
                <p className="text-white text-center mb-8 text-lg drop-shadow-md" data-testid="text-description">
                  Discover, document, and share urban art worldwide
                </p>
                
                {/* Main Action Buttons */}
                <div className="space-y-4">
                  <Button 
                    onClick={() => window.location.href = '/api/login'}
                    className="w-full bg-neon-green text-black font-bold text-lg py-4 hover:bg-neon-green/90 glow-effect shadow-lg"
                    data-testid="button-signin"
                  >
                    Sign In / Create Account
                  </Button>
                  
                  <Button 
                    onClick={() => window.location.href = '/onboarding'}
                    className="w-full bg-coral text-white font-bold text-lg py-4 hover:bg-coral/90 flex items-center justify-center shadow-lg"
                    data-testid="button-get-started"
                  >
                    <Crown size={20} className="mr-2" />
                    Get Started
                  </Button>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-3 gap-6 mt-8 text-center">
                  <div className="p-3">
                    <MapPin className="mx-auto text-neon-green mb-3 drop-shadow-lg" size={28} />
                    <p className="text-sm text-white font-medium drop-shadow-md">Explore Art</p>
                  </div>
                  <div className="p-3">
                    <Crown className="mx-auto text-coral mb-3 drop-shadow-lg" size={28} />
                    <p className="text-sm text-white font-medium drop-shadow-md">Earn Tokens</p>
                  </div>
                  <div className="p-3">
                    <Wallet className="mx-auto text-blue-400 mb-3 drop-shadow-lg" size={28} />
                    <p className="text-sm text-white font-medium drop-shadow-md">Web3 Ready</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </main>
    </div>
  );
}